import React, { useEffect, useId, useRef } from "react";
import { X } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { getModalStyles, getPrimaryText } from "../../utils/themeUtils";
import { TOKENS } from "../../utils/designTokens";
import { Button } from "../ui";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /**
   * Width cap. Defaults to the wide size every existing dialog uses; pass a
   * narrower one (e.g. "max-w-2xl") for a short prompt.
   */
  maxWidthClass?: string;
};

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared modal. Dialog semantics (role="dialog" + aria-modal), Escape
 * to close, focus moved into the dialog on open and trapped while open,
 * and restored to the opener on close — every consumer inherits this
 * (audit H10).
 */
const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, maxWidthClass = "max-w-5xl" }) => {
  const { isDarkMode } = useTheme();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    // Move focus into the dialog; remember the opener to restore later.
    openerRef.current = (document.activeElement as HTMLElement) || null;
    const dialog = dialogRef.current;
    const first = dialog?.querySelector<HTMLElement>(FOCUSABLE);
    (first || dialog)?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      // Focus trap: keep Tab cycling inside the dialog.
      if (e.key === "Tab" && dialog) {
        const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (focusable.length === 0) return;
        const firstEl = focusable[0];
        const lastEl = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      openerRef.current?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${TOKENS.overlay.backdrop}`}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={`${getModalStyles(isDarkMode)} rounded-xl shadow-lg w-full ${maxWidthClass} p-6 relative`}
      >
        {/* Close button */}
        <Button
          type="button"
          variant="neutral"
          size="sm"
          iconOnly
          onClick={onClose}
          aria-label="Close dialog"
          className="absolute top-3 right-3"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </Button>

        {/* Title */}
        {title && <h2 id={titleId} className={`text-lg font-semibold mb-4 ${getPrimaryText(isDarkMode)}`}>{title}</h2>}

        {/* Body */}
        <div>{children}</div>
      </div>
    </div>
  );
};

export default Modal;
